import React from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { Home, Car, Bell, User, LogOut, BarChart3, Plus } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Logo } from "./Logo";
import { AppHeader } from "./AppHeader";
import { api } from "../api/client";
import { useEffect, useState } from "react";

const NAV_ITEMS = [
  { to: "/", label: "Accueil", icon: Home, testId: "nav-home" },
  { to: "/vehicles", label: "Véhicules", icon: Car, testId: "nav-vehicles" },
  { to: "/notifications", label: "Alertes", icon: Bell, testId: "nav-notifications", badge: true },
  { to: "/dashboard", label: "Analytique", icon: BarChart3, testId: "nav-dashboard" },
  { to: "/profile", label: "Profil", icon: User, testId: "nav-profile" },
];

// Routes on which the "Ajouter un véhicule" FAB is allowed.
// Deliberately narrow: Home + Vehicles list only (avoid noise on forms,
// vehicle detail, notifications, profile, etc.).
const FAB_ALLOWED_PATHS = new Set(["/", "/vehicles"]);

export function Layout({ children }) {
  const [unreadCount, setUnreadCount] = useState(0);
  useEffect(() => {
    let cancelled = false;
    const fetchCount = async () => {
      try {
        const { data } = await api.get("/notifications/unread-count");
        if (!cancelled) setUnreadCount(data?.count || 0);
      } catch { /* ignore — badge just shows 0 */ }
    };
    fetchCount();
    // Refresh on route change + every 60s (cheap endpoint).
    const t = setInterval(fetchCount, 60000);
    const onFocus = () => fetchCount();
    window.addEventListener("focus", onFocus);
    return () => { cancelled = true; clearInterval(t); window.removeEventListener("focus", onFocus); };
  }, []);
  // Re-fetch when user navigates (marking one read should update the badge).
  const { signOut, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    api.get("/notifications/unread-count").then(({ data }) => setUnreadCount(data?.count || 0)).catch(() => {});
  }, [location.pathname]);

  const onSignOut = async () => {
    await signOut();
    navigate("/login");
  };

  return (
    <div style={{ minHeight: "100vh", background: "var(--mf-bg)", display: "flex" }}>
      {/* Desktop sidebar (>= 1024px) */}
      <aside
        style={{
          width: 260, flexShrink: 0, background: "var(--mf-brand)",
          color: "#fff", padding: "28px 20px",
          display: "none", flexDirection: "column",
          gap: 8, position: "sticky", top: 0, height: "100vh",
        }}
        className="mf-sidebar"
        data-testid="sidebar"
      >
        <div style={{ marginBottom: 32, paddingLeft: 4 }}>
          <Logo variant="light" width={140} />
          <div style={{ marginTop: 8, color: "rgba(255,255,255,0.6)", fontSize: 12, letterSpacing: 0.4 }}>
            Votre flotte, sans effort.
          </div>
        </div>

        <nav style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1 }}>
          {NAV_ITEMS.map(({ to, label, icon: Icon, testId, badge }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              data-testid={testId}
              style={({ isActive }) => ({
                display: "flex", alignItems: "center", gap: 12,
                padding: "12px 14px", borderRadius: 12,
                color: isActive ? "#fff" : "rgba(255,255,255,0.65)",
                background: isActive ? "rgba(255,255,255,0.10)" : "transparent",
                textDecoration: "none", fontWeight: 600, fontSize: 15,
                transition: "background 200ms, color 200ms",
                position: "relative",
              })}
            >
              <Icon size={20} />
              {label}
              {badge && unreadCount > 0 && (
                <span
                  data-testid="nav-notifications-badge"
                  style={{
                    marginLeft: "auto", minWidth: 20, height: 20, padding: "0 6px",
                    borderRadius: 10, background: "var(--mf-urgent-solid)",
                    color: "#fff", fontSize: 11, fontWeight: 800,
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                  }}
                >{unreadCount > 99 ? "99+" : unreadCount}</span>
              )}
            </NavLink>
          ))}
        </nav>

        <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: 16, marginTop: 16 }}>
          <div style={{ padding: "0 4px", marginBottom: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#fff", lineHeight: 1.2 }}>
              {user?.name || "Utilisateur"}
            </div>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.55)", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {user?.email}
            </div>
          </div>
          <button
            onClick={onSignOut}
            data-testid="sidebar-logout"
            style={{
              width: "100%", display: "flex", alignItems: "center", gap: 10,
              padding: "10px 14px", background: "rgba(255,255,255,0.06)",
              color: "rgba(255,255,255,0.85)", border: "none", borderRadius: 12,
              cursor: "pointer", fontWeight: 600, fontSize: 14,
            }}
          >
            <LogOut size={16} />
            Se déconnecter
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main
        style={{
          flex: 1, minWidth: 0,
          paddingBottom: "calc(96px + env(safe-area-inset-bottom, 0px))",
        }}
        data-testid="main-content"
      >
        <AppHeader background="surface" />
        {children}
      </main>

      {/* Mobile bottom tab bar (< 1024px) */}
      <nav
        className="mf-bottom-nav"
        data-testid="bottom-nav"
        style={{
          position: "fixed", bottom: 0, left: 0, right: 0,
          background: "var(--mf-bg-card)", borderTop: "1px solid var(--mf-divider)",
          display: "flex", padding: "8px 0 calc(8px + env(safe-area-inset-bottom))",
          zIndex: 40,
        }}
      >
        {NAV_ITEMS.map(({ to, label, icon: Icon, testId, badge }) => {
          const active =
            to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);
          return (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              data-testid={`${testId}-mobile`}
              style={{
                flex: 1, display: "flex", flexDirection: "column", alignItems: "center",
                gap: 3, textDecoration: "none",
                color: active ? "var(--mf-brand)" : "var(--mf-text-muted)",
                fontSize: 11, fontWeight: 600, padding: "4px 0",
                position: "relative",
              }}
            >
              <span style={{ position: "relative" }}>
                <Icon size={22} strokeWidth={active ? 2.4 : 2} />
                {badge && unreadCount > 0 && (
                  <span style={{
                    position: "absolute", top: -4, right: -8,
                    minWidth: 16, height: 16, padding: "0 4px",
                    borderRadius: 8, background: "var(--mf-urgent-solid)",
                    color: "#fff", fontSize: 9, fontWeight: 800,
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                  }}>{unreadCount > 9 ? "9+" : unreadCount}</span>
                )}
              </span>
              <span>{label}</span>
            </NavLink>
          );
        })}
      </nav>

      <style>{`
        @media (min-width: 1024px) {
          .mf-sidebar { display: flex !important; }
          .mf-bottom-nav { display: none !important; }
          main[data-testid="main-content"] { padding-bottom: 40px !important; }
          .mf-fab-add-vehicle {
            right: 28px !important;
            bottom: calc(28px + env(safe-area-inset-bottom, 0px)) !important;
            width: 60px !important;
            height: 60px !important;
          }
        }
        .mf-fab-add-vehicle:focus-visible {
          outline: 2px solid #fff;
          outline-offset: 3px;
          box-shadow: 0 0 0 4px var(--mf-brand), 0 8px 24px rgba(11,25,44,0.35);
        }
      `}</style>

      {user && FAB_ALLOWED_PATHS.has(location.pathname) && (
        <AddVehicleFab
          onClick={() => navigate("/vehicles/new")}
          testId={location.pathname === "/" ? "home-add-vehicle-fab" : "vehicles-add-vehicle-fab"}
        />
      )}
    </div>
  );
}

function AddVehicleFab({ onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      aria-label="Ajouter un véhicule"
      title="Ajouter un véhicule"
      className="mf-fab-add-vehicle"
      style={{
        position: "fixed",
        right: 20,
        bottom: "calc(72px + env(safe-area-inset-bottom, 0px) + 12px)",
        width: 56,
        height: 56,
        borderRadius: 999,
        background: "var(--mf-brand)",
        color: "#fff",
        border: "none",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: "0 8px 24px rgba(11,25,44,0.35)",
        cursor: "pointer",
        zIndex: 50,
        transition: "transform 150ms ease, box-shadow 150ms ease",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-1px)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; }}
    >
      <Plus size={26} strokeWidth={2.5} />
    </button>
  );
}

export function PageHeader({ title, subtitle, right, testID }) {
  return (
    <header
      data-testid={testID}
      style={{
        padding: "24px 20px 12px",
        display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12,
      }}
    >
      <div style={{ minWidth: 0, flex: 1 }}>
        <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, letterSpacing: -0.5, color: "var(--mf-text)" }}>
          {title}
        </h1>
        {subtitle && (
          <div style={{ marginTop: 4, color: "var(--mf-text-secondary)", fontSize: 14, fontWeight: 500 }}>
            {subtitle}
          </div>
        )}
      </div>
      {right && <div>{right}</div>}
    </header>
  );
}
